<template>
    <div class="content-preview">
        <div class="navigation">
            <div class="left">
                <div class="back-button" @click="close">
                    <i class="material-icons">arrow_back</i>
                </div>
            </div>
            <div class="right">
                <div class="switch-wrapper" :class="{ active: knowledgeBaseClone.enabled }">
                    <div class="">
                        <div class="title" v-if="knowledgeBaseClone.enabled">{{ $t("chatbotv2.usedByAI") }}</div>
                        <div class="title" v-else>{{ $t("chatbotv2.notUsed") }}</div>
                    </div>
                    <a-switch
                        size="small"
                        :checked="knowledgeBaseClone.enabled"
                        @click="changeState"
                        :disabled="busy"
                    />
                </div>
                <div class="buttons">
                    <SupsisButton small ghost @click="deleteKnowledgeBase" :disabled="busy">
                        <i class="material-icons">delete</i>
                    </SupsisButton>
                    <SupsisButton small ghost disabled>
                        <i class="material-icons">refresh</i>
                    </SupsisButton>
                    <SupsisButton small ghost :disabled="busy" @click="editMode = !editMode">
                        <i class="material-icons">edit</i>
                    </SupsisButton>
                    <SupsisButton small primary :disabled="!isChanged() || busy" @click="updateArticle">
                        {{ $t("message.saveAndClose") }}
                    </SupsisButton>
                </div>
            </div>
        </div>
        <div class="scroll-content">
            <div class="doc-title" v-if="!editMode">{{ knowledgeBase.article.title }}</div>
            <input class="doc-title title-input" v-else v-model="knowledgeBase.article.title" type="text" />
            <div class="doc-content">
                <div class="content-preview">
                    <i class="material-icons">preview</i>
                    {{ $t("message.contentPreview") }}
                </div>
                <div class="tpl-markdown markdown" v-if="!editMode">
                    {{ knowledgeBase.article.content }}
                </div>
                <textarea v-else class="answer-input" v-model="knowledgeBase.article.content" type="text" />
            </div>
        </div>
    </div>
</template>
<script>
import Vue from "vue";
import SupsisButton from "../../../Settings/components/SupsisButton.vue";
import { CHATBOT_API } from "../../../../../api/chatbotv2";
import _ from "lodash";
import { sendSocketMessage } from "../../../../../utils/socketStream";
import { mapState } from "vuex";

export default Vue.extend({
    name: "PreviewArticles",
    components: { SupsisButton },
    data() {
        return {
            data: null,
            loading: true,
            knowledgeBaseClone: _.cloneDeep(this.knowledgeBase),
            busy: false,
            editMode: false,
        };
    },
    props: {
        knowledgeBase: {
            type: Object,
            default: () => ({}),
        },
        storyId: {
            type: String,
            default: "",
        },
    },
    computed: {
        ...mapState({
            site: (state) => state.site.site,
        }),
    },
    methods: {
        isChanged() {
            return !_.isEqual(this.knowledgeBase, this.knowledgeBaseClone);
        },
        changeState(e) {
            if (e.preventDefault) e.preventDefault();
            if (e.stopPropagation) e.stopPropagation();
            this.knowledgeBaseClone.enabled = !this.knowledgeBaseClone.enabled;
        },
        close() {
            this.$emit("close");
        },
        fetch() {
            this.loading = true;
            CHATBOT_API.KNOWLEDGE_BASE.getKnowledgeBaseById(this.storyId, this.knowledgeBase._id)
                .then((res) => {
                    console.log("getKnowledgeBaseById", res.data);
                    this.data = res.data;
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        save() {
            this.busy = true;
            const hide = this.$message.loading(
                this.knowledgeBaseClone.enabled
                    ? "Selected knowledge bases are being enabled..."
                    : "Selected knowledge bases are being disabled...",
                0
            );
            CHATBOT_API.KNOWLEDGE_BASE.changeStateMultiKnowledgeBase(
                this.storyId,
                [this.knowledgeBase._id],
                this.knowledgeBaseClone.enabled
            )
                .then((isSuccess) => {
                    if (!isSuccess) return Promise.reject("Failed to change state.");
                    this.$emit("changed", this.knowledgeBaseClone.enabled);
                })
                .catch((error) => {
                    this.$message.error(
                        this.knowledgeBaseClone.enabled
                            ? "Selected knowledge bases are not enabled."
                            : "Selected knowledge bases are not disabled."
                    );
                })
                .finally(() => {
                    this.busy = false;
                    hide();
                });
        },
        deleteKnowledgeBase() {
            this.busy = true;
            const hide = this.$message.loading("Selected knowledge bases are being deleted...", 0);
            CHATBOT_API.KNOWLEDGE_BASE.deleteMultiKnowledgeBase(this.storyId, [this.knowledgeBase._id])
                .then((isSuccess) => {
                    if (!isSuccess) return Promise.reject("Failed to change state.");
                    this.$emit("deleted");
                    this.close();
                    this.fetch();
                })
                .catch((error) => {
                    this.$message.error("Selected knowledge bases are not deleted. Please try again.");
                })
                .finally(() => {
                    this.busy = false;
                    hide();
                });
        },
        updateArticle() {
            const title = this.knowledgeBase.article.title;
            const content = this.knowledgeBase.article.content;
            const titleClone = this.knowledgeBaseClone.article.title;
            const contentClone = this.knowledgeBaseClone.article.content;
            if (this.knowledgeBase.article.title === "" || this.knowledgeBase.article.content === "") {
                this.$message.error("Please fill in the title and content fields.");
                return;
            } else if (this.knowledgeBaseClone.enabled === false && title === titleClone && content === contentClone) {
                this.$emit("changed", this.knowledgeBase.enabled);
                sendSocketMessage("chatbot/v2 kb-article-removed", {
                    siteId: this.site._id,
                    storyId: this.storyId,
                    ids: [this.knowledgeBase._id],
                    isAssistantAPI: !!this.$store.state.CB.stories.find((story) => story._id === this.storyId)?.ai
                        ?.assistantId,
                });
                this.$message.success("Knowledge base article removed.");
                this.close();
                this.fetch();
            } else if (this.knowledgeBaseClone.enabled === true && title === titleClone && content === contentClone) {
                this.save();
                this.$emit("changed", this.knowledgeBaseClone.enabled);
                sendSocketMessage("chatbot/v2 kb-article-added", {
                    siteId: this.site._id,
                    storyId: this.storyId,
                    ids: [this.knowledgeBase._id],
                    isAssistantAPI: !!this.$store.state.CB.stories.find((story) => story._id === this.storyId)?.ai
                        ?.assistantId,
                });
                this.$message.success("Knowledge base article added.");
                this.close();
                this.fetch();
            } else if (title !== titleClone || content !== contentClone) {
                this.knowledgeBaseClone.enabled = false;
                this.save();
                this.$emit("changed", this.knowledgeBaseClone.enabled);
                sendSocketMessage("chatbot/v2 kb-article-removed", {
                    siteId: this.site._id,
                    storyId: this.storyId,
                    ids: [this.knowledgeBase._id],
                    isAssistantAPI: !!this.$store.state.CB.stories.find((story) => story._id === this.storyId)?.ai
                        ?.assistantId,
                });
                this.$message.success("Knowledge base article removed.");
                this.close();
                this.fetch();
            }

            CHATBOT_API.KNOWLEDGE_BASE.knowledgeBaseUpdate(
                this.storyId,
                this.knowledgeBase._id,
                this.knowledgeBase.article
            )
                .then((res) => {
                    console.log("knowledgeBaseUpdate", res.data);
                    this.$message.success("Knowledge base article updated.");
                })
                .catch((error) => {
                    this.$message.error("Failed to update knowledge base article.");
                })
                .finally(() => {
                    this.close();
                    this.fetch();
                });
        },
    },
    mounted() {
        this.fetch();
    },
});
</script>

<style scoped lang="scss">
.content-preview {
    display: flex;
    flex-direction: column;
    gap: 10px;
    width: 100%;
    height: 100%;
    position: relative;

    .answer-input {
        width: 100%;
        min-height: 450px;
        border: transparent;
        padding: 10px;
    }
    .navigation {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        align-items: center;

        .left {
            display: flex;
            align-items: center;

            .back-button {
                background: #f5f5f5;
                border-radius: 100%;
                height: 32px;
                width: 32px;
                cursor: pointer;
                justify-content: center;
                margin-right: auto;
                transition: all 0.3s;
                display: flex;
                align-items: center;
            }
        }

        .right {
            display: flex;
            align-items: center;
            gap: 5px;

            .switch-wrapper {
                background: #fff;
                border-radius: 15px;
                box-shadow: 0 4px 15px 0 rgba(0, 0, 0, 0.1);
                display: flex;
                height: 36px;
                padding: 0 8px 0 14px;
                width: 130px;
                align-items: center;
                justify-content: space-between;

                .title {
                    color: #22272d;
                    font-size: 15px;
                    font-weight: 700;
                    line-height: 19px;
                }

                &.active {
                    .title {
                        color: #00893c;
                    }
                }
            }

            .buttons {
                display: flex;
                align-items: center;
                gap: 5px;
            }
        }
    }

    .scroll-content {
        display: flex;
        flex-direction: column;
        gap: 10px;
        height: 100%;

        .doc-title {
            color: #22272d;
            display: block;
            font-size: 22px;
            font-weight: 600;
            line-height: 28px;
        }

        .title-input {
            background: transparent;
            border: transparent;
            width: 100%;
            overflow: scroll;
            max-height: 100px;
            border-bottom: 1px solid whitesmoke;
        }

        .doc-link {
            color: #006cff;
            display: block;
            font-size: 16px;
            line-height: 20px;
            margin-right: auto;
            margin-top: 8px;
            max-width: 100%;
            text-overflow: ellipsis;
            user-select: text;
            white-space: nowrap;
        }

        .doc-content {
            background: #f5f5f5;
            border-radius: 20px;
            margin-bottom: 36px;
            margin-top: 30px;
            padding: 24px;
            position: relative;
            max-height: 500px;
            .content-preview {
                display: flex;
                font-size: 14px;
                font-weight: 600;
                height: 34px;
                line-height: 18px;
                position: absolute;
                top: -17px;
                width: 168px;
                align-items: center;
                background: #fff;
                border-radius: 20px;
                box-shadow: 0 1px 10px 0 rgba(0, 0, 0, 0.12);
                color: #000;
                justify-content: center;
                left: 0;
                margin: 0 auto;
                right: 0;
                flex-direction: row;

                i {
                    font-size: 20px;
                    margin-right: 8px;
                }
            }

            .markdown {
                height: 100%;
                overflow-y: auto;
            }
        }
    }

    .save-btn {
        display: flex;
        justify-content: flex-end;
    }
}
</style>
